import { MappingResponse } from './types';

/**
 * MOCK DATA FOR DEVELOPMENT
 *
 * This mock data simulates the expected response from the backend mapping agent.
 * It's used for UI development and testing before the actual API is integrated.
 */

/**
 * MAPPING MOCK DATA
 * Based on the attached table with Source/Mapped row structure and collapsible groupings
 */
export const MOCK_MAPPING_RESPONSE: MappingResponse = {
  fileName: 'sample_codes.csv',

  summary: {
    totalCodes: 8,
    mappedCount: 5,
    unmappedCount: 2,
    notFoundCount: 1,
    alreadyStandardCount: 1,
  },

  results: [
    {
      type: 'Source',
      id: '--',
      name: '--',
      code: '1234567',
      vocabulary: 'SNOMED',
      standard: '--',
      domain: '--',
      class: '--',
      status: 'Not found',
      mappedCount: 0,
      reasoning: 'Code not found in OMOP concept table',
    },
    {
      type: 'Source',
      id: '3330199',
      name: 'Acute asthma',
      code: '304527002',
      vocabulary: 'Nebraska Lexicon',
      standard: 'No',
      domain: 'Condition',
      class: 'Clinical Finding',
      status: 'Unmapped',
      mappedCount: 0,
      reasoning: 'No OMOP provided mapping',
    },
    {
      type: 'Source',
      id: '3271036',
      name: 'Substance induced asthma',
      code: '424199006',
      vocabulary: 'Nebraska Lexicon',
      standard: 'No',
      domain: 'Condition',
      class: 'Clinical Finding',
      status: 'Unmapped',
      mappedCount: 0,
      reasoning: 'No OMOP provided mapping',
    },
    {
      type: 'Source',
      id: '44835411',
      name: 'Chronic obstructive asthma, unspecified',
      code: '493.2',
      vocabulary: 'ICD9',
      standard: 'No',
      domain: 'Condition',
      class: 'ICD9CM',
      status: 'Mapped',
      mappedCount: 2,
      reasoning: 'OMOP provided mapping',
      isCollapsible: true,
      mappedRows: [
        {
          type: 'Mapped',
          id: '317009',
          name: 'Asthma',
          code: '195967001',
          vocabulary: 'SNOMED',
          standard: 'Yes',
          domain: 'Condition',
          class: 'Clinical Finding',
          status: '-',
          mappedCount: '-',
          reasoning: 'OMOP provided mapping',
        },
        {
          type: 'Mapped',
          id: '255573',
          name: 'Chronic obstructive pulmonary disease',
          code: '13645005',
          vocabulary: 'SNOMED',
          standard: 'Yes',
          domain: 'Condition',
          class: 'Clinical Finding',
          status: '-',
          mappedCount: '-',
          reasoning: 'OMOP provided mapping',
        },
      ],
    },
    {
      type: 'Source',
      id: '45548118',
      name: 'Mixed asthma',
      code: 'J45.8',
      vocabulary: 'ICD10',
      standard: 'No',
      domain: 'Condition',
      class: 'ICD10CM',
      status: 'Mapped',
      mappedCount: 1,
      reasoning: 'OMOP provided mapping',
      isCollapsible: true,
      mappedRows: [
        {
          type: 'Mapped',
          id: '4110051',
          name: 'Mixed asthma',
          code: '195977004',
          vocabulary: 'SNOMED',
          standard: 'Yes',
          domain: 'Condition',
          class: 'Clinical Finding',
          status: '-',
          mappedCount: '-',
          reasoning: 'OMOP provided mapping',
        },
      ],
    },
    {
      type: 'Source',
      id: '45557625',
      name: 'Nonallergic asthma',
      code: 'J45.1',
      vocabulary: 'ICD10',
      standard: 'No',
      domain: 'Condition',
      class: 'ICD10CM',
      status: 'Mapped',
      mappedCount: 1,
      reasoning: 'OMOP provided mapping',
      isCollapsible: true,
      mappedRows: [
        {
          type: 'Mapped',
          id: '4145497',
          name: 'Intrinsic asthma',
          code: '266361008',
          vocabulary: 'SNOMED',
          standard: 'Yes',
          domain: 'Condition',
          class: 'Clinical Finding',
          status: '-',
          mappedCount: '-',
          reasoning: 'OMOP provided mapping',
        },
      ],
    },
    {
      type: 'Source',
      id: '45557624',
      name: 'Predominantly allergic asthma',
      code: 'J45.0',
      vocabulary: 'ICD10',
      standard: 'No',
      domain: 'Condition',
      class: 'ICD10CM',
      status: 'Mapped',
      mappedCount: 1,
      reasoning: 'OMOP provided mapping',
      isCollapsible: true,
      mappedRows: [
        {
          type: 'Mapped',
          id: '4191479',
          name: 'Allergic asthma',
          code: '389145006',
          vocabulary: 'SNOMED',
          standard: 'Yes',
          domain: 'Condition',
          class: 'Clinical Finding',
          status: '-',
          mappedCount: '-',
          reasoning: 'OMOP provided mapping',
        },
      ],
    },
    {
      type: 'Source',
      id: '4145497',
      name: 'Intrinsic asthma',
      code: '266361008',
      vocabulary: 'SNOMED',
      standard: 'Yes',
      domain: 'Condition',
      class: 'Clinical Finding',
      status: 'Already standard',
      mappedCount: 0,
      reasoning: 'Already standard concept',
    },
  ],
};

/**
 * Helper function to fetch mapping mock data (with Source/Mapped structure)
 */
export const fetchMockMappingResults = async (): Promise<MappingResponse> => {
  // Simulate network delay
  await new Promise((resolve) => setTimeout(resolve, 1000));
  return MOCK_MAPPING_RESPONSE;
};
